import type { Clock, IdGenerator } from "../ports/clock";
import type { GoalRepository, OnboardingWeightReferenceRepository, WeekPlanRepository } from "../ports/repositories";
import type { PlanningConfigurationProvider } from "../ports/planning-configuration";
import type { TransactionManager } from "../ports/transaction-manager";
import type { GoalPhase } from "../../domain/goals/goal";
import { validateGoalPhase } from "../../domain/goals/goal";
import { calculateProteinTarget } from "../../domain/goals/protein-policy";
import type { CalendarDate } from "../../domain/shared/calendar-date";
import { createWeekPlan, type WeekPlan } from "../../domain/planning/week-plan";
import { createOnboardingWeightReference } from "../../domain/weight/onboarding-reference";

export async function startTracking(
  dependencies: {
    readonly goals: GoalRepository;
    readonly weekPlans: WeekPlanRepository;
    readonly onboardingReference: OnboardingWeightReferenceRepository;
    readonly configuration: PlanningConfigurationProvider;
    readonly transaction: TransactionManager;
    readonly clock: Clock;
    readonly ids: IdGenerator;
  },
  input: {
    readonly goal: GoalPhase;
    readonly planStartDate: CalendarDate;
    readonly baselineDailyCalories: number;
    readonly referenceWeightKilograms: number;
    readonly catchUpThroughDate?: CalendarDate;
  },
): Promise<WeekPlan> {
  return dependencies.transaction.run(async () => {
    if (await dependencies.weekPlans.getOpen()) throw new Error("Tracking has already been initialized");
    const goal = validateGoalPhase(input.goal);
    const configuration = await dependencies.configuration.get();
    const now = dependencies.clock.now();
    const onboardingReference = createOnboardingWeightReference({
      date: dependencies.clock.today(),
      weightKilograms: input.referenceWeightKilograms,
      createdAt: now,
    });
    const protein = calculateProteinTarget({
      goalMode: goal.mode,
      referenceTrendWeightKilograms: input.referenceWeightKilograms,
    });
    const plan = createWeekPlan({
      id: dependencies.ids.next(),
      startDate: input.planStartDate,
      baselineDailyCalories: input.baselineDailyCalories,
      fixedDailyProteinGrams: protein.gramsPerDay,
      macroDistribution: configuration.macroDistribution,
      dailyFloorPolicy: configuration.dailyFloorPolicy,
      recommendationPolicyVersion: configuration.recommendationPolicy.version,
      goalPhaseId: goal.id,
      now,
      catchUpThroughDate: input.catchUpThroughDate,
    });
    await dependencies.goals.save(goal);
    await dependencies.onboardingReference.saveOnce(onboardingReference);
    await dependencies.weekPlans.save(plan);
    return plan;
  });
}
